import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ProjectStatuses } from '@resolve-ai/shared';
import { projectsApi } from '../lib/projects-api';
import { integrationsApi } from '../lib/integrations-api';
import type { ProjectListItem, ProjectWithConfigs } from '../lib/projects-api';
import type { Repository, JiraProject } from '../lib/integrations-api';
import { useIntegrationStatuses } from './useIntegrationStatuses';

interface ProjectCounts {
  active: number;
  inactive: number;
  archived: number;
}

interface ProjectsPageData {
  projects: ProjectListItem[];
  counts: ProjectCounts;
}

interface CreateProjectModalData {
  repositories: Repository[];
  jiraProjects: JiraProject[];
}

interface CreateProjectPayload {
  name: string;
  description: string;
  selectedRepos: Repository[];
  selectedJiraProject: JiraProject | null;
}

type ModalType = 'create' | 'edit' | 'delete' | null;

const EMPTY_PROJECT_COUNTS: ProjectCounts = {
  active: 0,
  inactive: 0,
  archived: 0,
};

export function useProjectsPage() {
  const queryClient = useQueryClient();

  const [toast, setToast] = useState<{
    isOpen: boolean;
    message: string;
    variant: 'success' | 'error' | 'warning' | 'info';
  }>({ isOpen: false, message: '', variant: 'info' });
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'inactive' | 'archived'>('all');

  const { githubStatus, jiraStatus, isLoading: isLoadingIntegrations } = useIntegrationStatuses();

  const [activeModal, setActiveModal] = useState<ModalType>(null);
  const [selectedProject, setSelectedProject] = useState<ProjectListItem | null>(null);

  const [createForm, setCreateForm] = useState({
    name: '',
    description: '',
    selectedRepos: [] as Repository[],
    selectedJiraProject: null as JiraProject | null,
  });
  const [editForm, setEditForm] = useState({
    name: '',
    description: '',
    status: ProjectStatuses.ACTIVE as typeof ProjectStatuses[keyof typeof ProjectStatuses],
  });

  const [openMenuId, setOpenMenuId] = useState<string | null>(null);

  const showToast = (message: string, variant: 'success' | 'error' | 'warning' | 'info' = 'info') => {
    setToast({ isOpen: true, message, variant });
  };

  const closeToast = () => {
    setToast((current) => ({ ...current, isOpen: false }));
  };

  const toggleMenu = (id: string) => {
    setOpenMenuId((current) => (current === id ? null : id));
  };

  const closeModal = () => {
    setActiveModal(null);
    setSelectedProject(null);
  };

  useEffect(() => {
    const handleClickOutside = () => setOpenMenuId(null);
    if (openMenuId) {
      document.addEventListener('click', handleClickOutside);
      return () => document.removeEventListener('click', handleClickOutside);
    }
    return undefined;
  }, [openMenuId]);

  const isGitHubConnected = githubStatus?.status === 'connected';
  const isJiraConnected = jiraStatus?.status === 'connected';
  const canCreateProject = isGitHubConnected && isJiraConnected;

  const projectsQuery = useQuery<ProjectsPageData>({
    queryKey: ['projects-page', statusFilter],
    queryFn: async () => {
      const [projects, counts] = await Promise.all([
        projectsApi.list(statusFilter),
        projectsApi.getCounts(),
      ]);
      return { projects, counts };
    },
    placeholderData: (previousData) => previousData,
  });

  const createModalDataQuery = useQuery<CreateProjectModalData>({
    queryKey: ['create-project-modal-data'],
    queryFn: async () => {
      const [repositories, jiraProjects] = await Promise.all([
        integrationsApi.getRepositories(),
        integrationsApi.getJiraProjects(),
      ]);
      return { repositories, jiraProjects };
    },
    enabled: false,
    staleTime: 1000 * 60 * 5,
  });

  const invalidateProjectsPageData = async () => {
    await queryClient.invalidateQueries({ queryKey: ['projects-page'] });
  };

  const createProjectMutation = useMutation({
    mutationFn: async (payload: CreateProjectPayload) => {
      let createdProject: ProjectWithConfigs | null = null;

      try {
        createdProject = await projectsApi.create({
          name: payload.name,
          description: payload.description || undefined,
        });

        const createdProjectId = createdProject.id;

        await Promise.all(
          payload.selectedRepos.map((repo) =>
            projectsApi.addGitHubConfig(createdProjectId, {
              repoOwner: repo.owner.login,
              repoName: repo.name,
              defaultBranch: repo.default_branch,
            })
          )
        );

        if (payload.selectedJiraProject) {
          await projectsApi.upsertJiraConfig(createdProject.id, {
            jiraProjectKey: payload.selectedJiraProject.key,
            jiraProjectName: payload.selectedJiraProject.name,
          });
        }
      } catch (error) {
        if (createdProject) {
          try {
            await projectsApi.delete(createdProject.id);
          } catch (cleanupError) {
            console.error('Failed to cleanup project:', cleanupError);
          }
        }
        throw error;
      }
    },
    onSuccess: async () => {
      closeModal();
      setCreateForm({
        name: '',
        description: '',
        selectedRepos: [],
        selectedJiraProject: null,
      });
      await invalidateProjectsPageData();
      showToast('Project created successfully', 'success');
    },
    onError: (error) => {
      console.error('Failed to create project:', error);
      showToast('Failed to create project', 'error');
    },
  });

  const updateProjectMutation = useMutation({
    mutationFn: async (payload: {
      projectId: string;
      name: string;
      description: string;
      status: typeof ProjectStatuses[keyof typeof ProjectStatuses];
    }) =>
      projectsApi.update(payload.projectId, {
        name: payload.name,
        description: payload.description || undefined,
        status: payload.status,
      }),
    onSuccess: async () => {
      closeModal();
      await invalidateProjectsPageData();
      showToast('Project updated successfully', 'success');
    },
    onError: (error) => {
      console.error('Failed to update project:', error);
      showToast('Failed to update project', 'error');
    },
  });

  const deleteProjectMutation = useMutation({
    mutationFn: async (projectId: string) => projectsApi.delete(projectId),
    onSuccess: async () => {
      closeModal();
      await invalidateProjectsPageData();
      showToast('Project deleted successfully', 'success');
    },
    onError: (error) => {
      console.error('Failed to delete project:', error);
      showToast('Failed to delete project', 'error');
    },
  });

  const isCreateFormValid =
    createForm.name.trim() !== '' &&
    createForm.selectedRepos.length > 0 &&
    createForm.selectedJiraProject !== null;

  const handleOpenCreateModal = async () => {
    if (!canCreateProject) return;

    try {
      const result = await createModalDataQuery.refetch();
      if (result.error) {
        throw result.error;
      }
      setActiveModal('create');
    } catch (err) {
      console.error('Failed to fetch repositories/projects:', err);
      showToast('Failed to load available repositories and Jira projects', 'error');
    }
  };

  const handleCreateProject = () => {
    if (!isCreateFormValid || createProjectMutation.isPending) return;

    createProjectMutation.mutate({
      name: createForm.name,
      description: createForm.description,
      selectedRepos: createForm.selectedRepos,
      selectedJiraProject: createForm.selectedJiraProject,
    });
  };

  const handleEditClick = (project: ProjectListItem) => {
    setSelectedProject(project);
    setEditForm({
      name: project.name,
      description: project.description || '',
      status: project.status,
    });
    setActiveModal('edit');
  };

  const handleUpdateProject = () => {
    if (!selectedProject || !editForm.name) {
      showToast('Project name is required', 'error');
      return;
    }

    if (updateProjectMutation.isPending) return;

    updateProjectMutation.mutate({
      projectId: selectedProject.id,
      name: editForm.name,
      description: editForm.description,
      status: editForm.status,
    });
  };

  const handleDeleteClick = (project: ProjectListItem) => {
    setSelectedProject(project);
    setActiveModal('delete');
  };

  const handleDeleteProject = () => {
    if (!selectedProject || deleteProjectMutation.isPending) return;
    deleteProjectMutation.mutate(selectedProject.id);
  };

  const projects = projectsQuery.data?.projects ?? [];
  const projectCounts = projectsQuery.data?.counts ?? EMPTY_PROJECT_COUNTS;
  const totalCount = projectCounts.active + projectCounts.inactive + projectCounts.archived;

  return {
    query: {
      projects,
      projectCounts,
      totalCount,
      statusFilter,
      setStatusFilter,
      isLoading: projectsQuery.isLoading,
      hasError: projectsQuery.isError && !projectsQuery.data,
      canCreateProject,
      isLoadingIntegrations,
      isLoadingRepos: createModalDataQuery.isFetching,
      repositories: createModalDataQuery.data?.repositories ?? [],
      jiraProjects: createModalDataQuery.data?.jiraProjects ?? [],
      openMenuId,
    },
    modal: {
      active: activeModal,
      selectedProject,
      close: closeModal,
    },
    form: {
      create: {
        data: createForm,
        setData: setCreateForm,
        isValid: isCreateFormValid,
      },
      edit: {
        data: editForm,
        setData: setEditForm,
      },
    },
    actions: {
      toggleMenu,
      openCreateModal: handleOpenCreateModal,
      openEditModal: handleEditClick,
      openDeleteModal: handleDeleteClick,
      createProject: handleCreateProject,
      updateProject: handleUpdateProject,
      deleteProject: handleDeleteProject,
    },
    mutations: {
      isCreating: createProjectMutation.isPending,
      isUpdating: updateProjectMutation.isPending,
      isDeleting: deleteProjectMutation.isPending,
    },
    toast: {
      state: toast,
      close: closeToast,
    },
  };
}
